import { defineStore } from 'pinia'

import * as retrievalRequestApi from '../services/api/retrieval-request'
import { IApproval } from './staff'

export interface IRetrievalRequestData {
  id: number
  status: string
  note: string
  reason: string
  approvals: Array<IApproval>
  temp_license_plate: boolean
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const useRetrievalRequestStore = defineStore('retrievalRequest', {
  state: () => ({
    retrievalRequest: {
      id: 0,
      status: '',
      note: '',
      reason: '',
      approvals: [],
      temp_license_plate: false,
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as IRetrievalRequestData,
    isLoading: false,
  }),

  actions: {
    setRetrievalRequest(data: IRetrievalRequestData) {
      this.retrievalRequest = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getRetrievalRequest(id: number) {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.get(id)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateRetrievalRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.update(this.retrievalRequest.id, payload)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyRetrievalRequest() {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.apply(this.retrievalRequest.id)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectRetrievalRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.reject(this.retrievalRequest.id, payload)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeRetrievalRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.remake(this.retrievalRequest.id, payload)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveRetrievalRequest() {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.approve(this.retrievalRequest.id)
        this.setRetrievalRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useRetrievalRequestListStore = defineStore('retrievalRequestList', {
  state: () => ({
    retrievalRequests: [] as IRetrievalRequestData[],
    count: 0,
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addRetrievalRequest(data: IRetrievalRequestData) {
      this.retrievalRequests.push(data)
    },

    setRetrievalRequests(data: IRetrievalRequestData[]) {
      this.retrievalRequests = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countRetrievalRequests() {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createRetrievalRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await retrievalRequestApi.create(payload)
        this.addRetrievalRequest(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listRetrievalRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await retrievalRequestApi.list(params)
        const { data, total } = result as any
        this.setRetrievalRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllRetrievalRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await retrievalRequestApi.listAll(params)
        const { data, total } = result as any
        this.setRetrievalRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
